import { useMemo } from "react";
import type { EChartsOption } from "echarts";
import { countBy, prefectureStats } from "../data/analytics";
import { CHART_COLORS } from "../data/constants";
import { formatNumber, formatPercent } from "../data/formatters";
import type { NormalizedRunner } from "../types";
import { Chart, chartGridColor, chartTextStyle } from "../components/Chart";
import { MetricCard } from "../components/MetricCard";
import { Panel } from "../components/Panel";

const TILE_POSITIONS: Array<[number, number]> = [
  [8, 1], [7, 2], [7, 3], [8, 3], [7, 4], [8, 4], [7, 5],
  [7, 6], [7, 7], [6, 6], [6, 7], [7, 8], [8, 7], [8, 8],
  [5, 3], [4, 4], [4, 5], [4, 6], [6, 8], [6, 4], [5, 6], [6, 7], [5, 7],
  [4, 7], [4, 8], [3, 7], [3, 8], [2, 8], [3, 9], [4, 9],
  [1, 7], [1, 8], [2, 7], [2, 6], [1, 6], [2, 10], [2, 9], [3, 10], [3, 11],
  [0, 7], [0, 8], [0, 9], [1, 9], [1, 10], [1, 11], [0, 11], [0, 13],
];

export function LocationPage({ runners }: { runners: NormalizedRunner[] }) {
  const locationCounts = countBy(runners, (runner) => runner.locationKind);
  const japan = locationCounts.get("japan") ?? 0;
  const overseas = locationCounts.get("overseas") ?? 0;
  const unknown = locationCounts.get("unknown") ?? 0;
  const prefectures = useMemo(() => prefectureStats(runners), [runners]);
  const ranked = useMemo(() => [...prefectures].filter((row) => row.value > 0).sort((a, b) => b.value - a.value), [prefectures]);
  const max = Math.max(1, ...prefectures.map((row) => row.value));

  const rankingOption: EChartsOption = useMemo(() => {
    const top = ranked.slice(0, 12).reverse();
    return {
      textStyle: chartTextStyle,
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
      grid: { left: 92, right: 48, top: 16, bottom: 38 },
      xAxis: { type: "value", axisLabel: chartTextStyle, splitLine: { lineStyle: { color: chartGridColor } } },
      yAxis: { type: "category", data: top.map((row) => row.label), axisLabel: chartTextStyle, axisLine: { lineStyle: { color: chartGridColor } } },
      series: [{ type: "bar", data: top.map((row) => row.value), label: { show: true, position: "right", color: "#dfe8f5", fontSize: 14 }, itemStyle: { color: CHART_COLORS[0] } }],
    };
  }, [ranked]);

  return (
    <div className="page-stack">
      <div className="metric-grid metric-grid--four">
        {/* 本頁共同母數 */}
        <MetricCard label="篩選後達標者" value={formatNumber(runners.length)} note="" tone="blue" />
        <MetricCard label="日本" value={formatNumber(japan)} note={formatPercent(japan / Math.max(1, runners.length))} tone="mint" />
        <MetricCard label="海外" value={formatNumber(overseas)} note={`${formatPercent(overseas / Math.max(1, runners.length))}，不推測國家`} tone="yellow" />
        <MetricCard label="未知／未公開" value={formatNumber(unknown)} note={formatPercent(unknown / Math.max(1, runners.length))} tone="pink" />
      </div>

      <div className="notice notice--info">
        日本地圖只呈現有效的都道府縣資料；海外 {formatNumber(overseas)} 人、未知 {formatNumber(unknown)} 人並未畫在地圖上。
      </div>

      <div className="content-grid content-grid--wide-left">
        <Panel title="日本都道府縣像素地圖" eyebrow="PREFECTURE TILE MAP" aside={<span className="sample">日本有效 N = {formatNumber(japan)}</span>}>
          {japan ? (
            <div className="tile-map-scroll">
              <div className="tile-map" role="img" aria-label="日本都道府縣達標者人數分級著色地圖">
                {prefectures.map((row, index) => {
                  const [column, line] = TILE_POSITIONS[index];
                  const alpha = row.value === 0 ? 0.08 : 0.28 + (row.value / max) * 0.72;
                  return (
                    <div
                      className="map-tile"
                      key={row.code}
                      style={{ gridColumn: column + 1, gridRow: line + 1, background: `rgba(68, 215, 182, ${alpha})` }}
                      title={`${row.label}：${row.value.toLocaleString()} 人`}
                    >
                      <span>{String(row.code).padStart(2, "0")}</span>
                      <b>{row.label.replace(/[縣府都]$/, "")}</b>
                      <em>{row.value}</em>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : <NoLocation />}
        </Panel>
        <Panel title="都道府縣人數排行" eyebrow="TOP PREFECTURES" aside={<span className="sample">前 12 名</span>}>
          {ranked.length ? <Chart option={rankingOption} height={430} label="日本都道府縣達標者人數排行榜" /> : <NoLocation />}
        </Panel>
      </div>

      <Panel title="占比口徑" eyebrow="DENOMINATORS">
        <div className="rank-table">
          <div className="rank-table__head"><span>都道府縣</span><span>人數</span><span>占日本有效地點</span><span>占全部達標者</span></div>
          {ranked.slice(0, 15).map((row) => (
            <div key={row.code}><strong>{row.label}</strong><span>{formatNumber(row.value)}</span><span>{formatPercent(row.value / Math.max(1, japan))}</span><span>{formatPercent(row.value / Math.max(1, runners.length))}</span></div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

function NoLocation() {
  return <div className="empty-state"><span>?</span><p>目前篩選結果沒有日本都道府縣資料。</p></div>;
}
